// Push Notification Subscription Management
import { BACKEND_URL } from '@/API_CONFIG';
const API = BACKEND_URL;

// Helper to convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const subscribeToPushNotifications = async () => {
  try {
    console.log('[Push] Starting push subscription process...');
    
    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      console.log('[Push] ❌ Service Worker not supported');
      return false;
    }

    // Check if Push API is supported
    if (!('PushManager' in window)) {
      console.log('[Push] ❌ Push API not supported');
      return false;
    }

    // Check notification permission
    console.log('[Push] Current permission:', Notification.permission);
    if (Notification.permission === 'denied') {
      console.log('[Push] ❌ Notification permission denied by user');
      return false;
    }

    // Request permission if not granted
    if (Notification.permission !== 'granted') {
      console.log('[Push] Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('[Push] Permission result:', permission);
      
      if (permission !== 'granted') {
        console.log('[Push] ❌ User denied notification permission');
        return false;
      }
    }

    // Get service worker registration
    console.log('[Push] Waiting for service worker...');
    const registration = await navigator.serviceWorker.ready;
    console.log('[Push] Service worker ready:', registration);

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      console.log('[Push] Already subscribed:', subscription);
      // Send existing subscription to backend
      await saveSubscriptionToBackend(subscription);
      return true;
    }

    // Get VAPID public key from backend
    console.log('[Push] Fetching VAPID public key...');
    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/api/push/vapid-public-key`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch VAPID public key');
    }
    
    const { publicKey } = await response.json();
    console.log('[Push] Got VAPID public key');

    // Subscribe to push notifications
    console.log('[Push] Subscribing to push notifications...');
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    console.log('[Push] ✅ Subscribed successfully:', subscription);

    // Save subscription to backend
    await saveSubscriptionToBackend(subscription);

    return true;
  } catch (error) {
    console.error('[Push] ❌ Subscription error:', error);
    return false;
  }
};

async function saveSubscriptionToBackend(subscription) {
  try {
    console.log('[Push] Saving subscription to backend...');
    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(subscription.toJSON())
    });

    if (!response.ok) {
      throw new Error('Failed to save subscription to backend');
    }

    console.log('[Push] ✅ Subscription saved to backend');
    return true;
  } catch (error) {
    console.error('[Push] ❌ Failed to save subscription:', error);
    return false;
  }
}

export const unsubscribeFromPushNotifications = async () => {
  try {
    console.log('[Push] Unsubscribing from push notifications...');
    
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      console.log('[Push] ✅ Unsubscribed from push');

      // Remove from backend
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    }

    return true;
  } catch (error) {
    console.error('[Push] ❌ Unsubscribe error:', error);
    return false;
  }
};
